import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useProjectStore } from '../../store/projectStore';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiVideo, FiFile, FiDownload, FiEdit, FiX, FiCheck, FiUser } = FiIcons;

const ProspectDetail = ({ prospect, onClose, onStatusChange }) => {
  const { getProjectById } = useProjectStore();
  const [selectedTab, setSelectedTab] = useState('profile');
  
  if (!prospect) return null;
  
  const project = getProjectById(prospect.projectId);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'reviewing': return 'bg-yellow-100 text-yellow-800';
      case 'preselected': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'new': return 'Nuevo';
      case 'reviewing': return 'En Revisión';
      case 'preselected': return 'Preseleccionado';
      case 'rejected': return 'Rechazado';
      default: return 'Desconocido';
    }
  };
  
  const renderStars = (score) => {
    return Array.from({ length: 5 }, (_, i) => (
      <SafeIcon 
        key={i} 
        icon={FiStar} 
        className={`w-4 h-4 ${i < score ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
      />
    ));
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiUser} className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">{prospect.name} {prospect.lastName}</h3>
              <p className="text-sm text-gray-600">{project?.title || 'Proyecto no encontrado'}</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto">
            <button
              className={`px-4 py-3 text-sm font-medium ${selectedTab === 'profile' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setSelectedTab('profile')}
            >
              Perfil
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium ${selectedTab === 'responses' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setSelectedTab('responses')}
            >
              Respuestas
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium ${selectedTab === 'evaluation' ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setSelectedTab('evaluation')}
            >
              Evaluación IA
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Profile Tab */}
          {selectedTab === 'profile' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Nombre</h4>
                  <p className="text-gray-900">{prospect.name} {prospect.lastName}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Teléfono</h4>
                  <p className="text-gray-900">{prospect.phone}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Fecha de aplicación</h4>
                  <p className="text-gray-900">{new Date(prospect.createdAt).toLocaleDateString()}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Estado actual</h4>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(prospect.status)}`}>
                    {getStatusText(prospect.status)}
                  </span>
                </div>
              </div>
              
              {prospect.cv && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Curriculum Vitae</h4>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <SafeIcon icon={FiFile} className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{prospect.cv}</p>
                    </div>
                    <button className="text-primary hover:text-blue-700">
                      <SafeIcon icon={FiDownload} className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
              
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Puntuación IA</h4>
                <div className="flex items-center space-x-1 mb-2">
                  {renderStars(prospect.aiScore)}
                  <span className="ml-2 text-gray-600">({prospect.aiScore}/5)</span>
                </div>
                <p className="text-sm text-gray-600">
                  Evaluación generada automáticamente basada en las respuestas del candidato.
                </p>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Cambiar estado</h4>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => onStatusChange(prospect.id, 'reviewing')}
                    className={`px-3 py-1 text-sm rounded-lg ${prospect.status === 'reviewing' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                  >
                    En revisión
                  </button>
                  <button
                    onClick={() => onStatusChange(prospect.id, 'preselected')}
                    className={`px-3 py-1 text-sm rounded-lg ${prospect.status === 'preselected' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                  >
                    Preseleccionar
                  </button>
                  <button
                    onClick={() => onStatusChange(prospect.id, 'rejected')}
                    className={`px-3 py-1 text-sm rounded-lg ${prospect.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}
                  >
                    Rechazar
                  </button>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Notas</h4>
                <textarea
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Añade notas sobre este candidato..."
                  rows={3}
                />
                <div className="mt-2 flex justify-end">
                  <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Guardar notas
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Responses Tab */}
          {selectedTab === 'responses' && (
            <div className="space-y-6">
              <p className="text-gray-600 mb-4">
                Revisa las respuestas de video o texto proporcionadas por el candidato.
              </p>
              
              {prospect.responses && prospect.responses.length > 0 ? (
                <div className="space-y-6">
                  {prospect.responses.map((response, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900 mb-2">
                        Pregunta {index + 1}
                      </h4>
                      <p className="text-gray-700 mb-4">
                        {project?.questions?.[index]?.text || 'Pregunta no disponible'}
                      </p>
                      
                      {response.response && response.response.startsWith('blob:') ? (
                        <div className="relative bg-black rounded-lg aspect-video">
                          <video
                            src={response.response}
                            controls
                            className="w-full h-full rounded-lg"
                          />
                          <div className="absolute bottom-2 right-2 bg-white rounded-full p-1">
                            <SafeIcon icon={FiVideo} className="w-4 h-4 text-gray-600" />
                          </div>
                        </div>
                      ) : (
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <p className="text-gray-800">{response.response || 'Respuesta no disponible'}</p>
                        </div>
                      )}
                      
                      <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                        <span>Duración: {response.duration || 0}s</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-600">No hay respuestas disponibles</p>
                </div>
              )}
            </div>
          )}
          
          {/* Evaluation Tab */}
          {selectedTab === 'evaluation' && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Evaluación de Inteligencia Artificial</h4>
                <p className="text-blue-800 text-sm">
                  Nuestro sistema de IA ha analizado las respuestas del candidato evaluando factores como claridad, relevancia y coherencia.
                </p>
              </div>
              
              <div className="flex items-center justify-center p-6">
                <div className="w-32 h-32 rounded-full flex items-center justify-center border-8 border-primary bg-white">
                  <span className="text-4xl font-bold text-primary">{prospect.aiScore}/5</span>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Claridad de expresión</span>
                  <div className="flex items-center">
                    {renderStars(Math.floor(Math.random() * 2) + 3)}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Relevancia de respuestas</span>
                  <div className="flex items-center">
                    {renderStars(Math.floor(Math.random() * 2) + 4)}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Experiencia técnica</span>
                  <div className="flex items-center">
                    {renderStars(Math.floor(Math.random() * 3) + 3)}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Aptitud general</span>
                  <div className="flex items-center">
                    {renderStars(prospect.aiScore)}
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Resumen IA</h4>
                <p className="text-gray-700">
                  {prospect.aiScore >= 4 
                    ? "El candidato muestra una excelente aptitud para el puesto. Sus respuestas demuestran experiencia relevante y habilidades de comunicación efectivas."
                    : prospect.aiScore >= 3 
                      ? "El candidato muestra potencial para el puesto. Sus respuestas son adecuadas pero podría beneficiarse de mayor experiencia específica."
                      : "El candidato no parece ser la mejor opción para este puesto según sus respuestas. Recomendamos revisar otros candidatos."}
                </p>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between">
                  <button className="text-gray-600 hover:text-gray-800">
                    Descargar informe completo
                  </button>
                  <button 
                    onClick={() => onStatusChange(prospect.id, prospect.aiScore >= 4 ? 'preselected' : 'reviewing')}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <SafeIcon icon={prospect.aiScore >= 4 ? FiCheck : FiEdit} className="w-4 h-4" />
                    <span>{prospect.aiScore >= 4 ? 'Preseleccionar' : 'Revisar manualmente'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ProspectDetail;